import {Input} from 'components-lib';
import {FieldProps} from 'formik';
import React, {ReactNode} from 'react';
import {FastField, Field} from './Field';
import {useDebounceUserInput} from 'hooks';
import {IWithDisabled} from 'models';

interface IFormInputProps extends IWithDisabled {
  name: string;
  value?: string;
  label?: string;
  debounceDelay?: number;
  type?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  withIcon?: boolean;
  iconPosition?: 'start' | 'end';
  icon?: ReactNode;
  autoFocus?: boolean;
  /** When true, non-digit characters are stripped from input. */
  digitsOnly?: boolean;
  /** When used with digitsOnly, allows a single decimal point (period). */
  allowDecimal?: boolean;
}

export function FormInput({name, value, label, debounceDelay, disabled = false}: IFormInputProps) {
  return (
    <FastField
      name={name}
      value={value}
      label={label}
      disabled={disabled}
      debounceDelay={debounceDelay}
      Component={InputDecorated}
    />
  );
}

/**
 * Notes:
 * Use input without formik FastField if your component depends on other component
 * (see: https://stackoverflow.com/questions/59841681/when-not-to-use-formiks-fastfield)
 */

export function FormInputWithField({
  name,
  value,
  label,
  debounceDelay,
  type,
  min,
  max,
  disabled = false,
  withIcon = false,
  autoFocus = false,
  iconPosition,
  icon,
  digitsOnly = false,
  allowDecimal = false,
}: IFormInputProps) {
  return (
    <Field
      name={name}
      value={value}
      label={label}
      disabled={disabled}
      debounceDelay={debounceDelay}
      type={type}
      min={min}
      max={max}
      withIcon={withIcon}
      icon={icon}
      iconPosition={iconPosition}
      autoFocus={autoFocus}
      digitsOnly={digitsOnly}
      allowDecimal={allowDecimal}
      Component={InputDecorated}
    />
  );
}

interface IInputDecoratedProps extends FieldProps, IFormInputProps {}

const sanitizeDigitsOnlyValue = (raw: string, allowDecimal: boolean): string => {
  if (!allowDecimal) {
    return raw.replace(/\D/g, '');
  }

  const sanitized = raw.replace(/[^\d.]/g, '');
  const firstDotIndex = sanitized.indexOf('.');

  if (firstDotIndex === -1) {
    return sanitized;
  }

  return sanitized.slice(0, firstDotIndex + 1) + sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
};

function InputDecorated({
  field: {name, onChange},
  form: {values},
  debounceDelay,
  value: initialVal,
  digitsOnly = false,
  allowDecimal = false,
  max,
  ...rest
}: IInputDecoratedProps) {
  const fieldValue = values[name];
  const initialValue: string =
    fieldValue !== null && fieldValue !== undefined && fieldValue !== '' ? fieldValue : initialVal;
  const [value, changeHandler] = useDebounceUserInput(initialValue, onChange, debounceDelay);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (digitsOnly) {
      const sanitized = sanitizeDigitsOnlyValue(event.target.value, allowDecimal);
      const maxValue = max != null && max !== '' ? Number(max) : undefined;

      if (
        maxValue != null &&
        !Number.isNaN(maxValue) &&
        sanitized !== '' &&
        sanitized !== '.' &&
        Number(sanitized) > maxValue
      ) {
        return;
      }

      event.target.value = sanitized;
    }
    changeHandler(event);
  };

  const props = {
    ...rest,
    max,
    value,
    changeHandler: handleChange,
    ...(digitsOnly
      ? {
          inputProps: {
            inputMode: allowDecimal ? 'decimal' : 'numeric',
            pattern: allowDecimal ? '[0-9]*\\.?[0-9]*' : '[0-9]*',
          },
        }
      : {}),
  };

  return <Input withMargin {...props} />;
}
